import { useState } from "react";
import type { JobApprovalDto } from "@/lib/job-approvals";

export interface ApprovalCardProps {
  approval: JobApprovalDto;
  onDecide: (
    approvalId: number,
    decision: "Approve" | "Reject",
    comment: string | undefined,
    rowVersionBase64: string,
  ) => void;
  onAddComment: (approvalId: number, text: string) => void;
}

export function ApprovalCard({ approval, onDecide, onAddComment }: ApprovalCardProps) {
  const [comment, setComment] = useState("");
  const [newComment, setNewComment] = useState("");

  const isPending = approval.decidedAtUtc == null;

  function handleDecide(decision: "Approve" | "Reject") {
    onDecide(approval.jobApprovalId, decision, comment.trim() || undefined, approval.rowVersionBase64);
    setComment("");
  }

  function handleAddComment() {
    const text = newComment.trim();
    if (!text) return;
    onAddComment(approval.jobApprovalId, text);
    setNewComment("");
  }

  return (
    <div className="rounded-lg border border-border p-4 space-y-2" data-testid="approval-card">
      <div className="flex items-center justify-between">
        <span className="font-medium text-sm">{approval.approvalTypeLabel}</span>
        {!isPending && (
          <span className="text-xs font-medium text-muted-foreground">
            {approval.approved ? "Approved" : "Rejected"}
          </span>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Requested by <span className="font-medium">{approval.requestedByUserName}</span>
      </p>

      {isPending ? (
        <div className="space-y-2">
          <textarea
            className="w-full rounded border border-border px-2 py-1 text-sm resize-none"
            placeholder="Optional comment…"
            rows={2}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            aria-label="Decision comment"
          />
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded bg-green-600 px-3 py-1 text-xs font-medium text-white hover:bg-green-700"
              onClick={() => handleDecide("Approve")}
            >
              Approve
            </button>
            <button
              type="button"
              className="rounded bg-destructive px-3 py-1 text-xs font-medium text-destructive-foreground hover:opacity-90"
              onClick={() => handleDecide("Reject")}
            >
              Reject
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-1">
          <p className="text-xs">
            <span className="font-medium">Decision:</span>{" "}
            {approval.approved ? "Approved" : "Rejected"}
            {approval.decidedByUserName ? ` by ${approval.decidedByUserName}` : ""}
          </p>
          {approval.decisionComment && (
            <p className="text-xs text-muted-foreground">{approval.decisionComment}</p>
          )}
        </div>
      )}

      <div className="pt-2 space-y-1">
        <div className="flex gap-2">
          <input
            type="text"
            className="flex-1 rounded border border-border px-2 py-1 text-xs"
            placeholder="Add a comment…"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            aria-label="New comment"
          />
          <button
            type="button"
            className="rounded bg-secondary px-2 py-1 text-xs font-medium hover:bg-secondary/80"
            onClick={handleAddComment}
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
}
